/**
 * Account settings sync — auth node as the source of truth
 *
 * Splits localStorage keys into two buckets:
 *
 *   AUTH NODE (profile metadata)
 *     locale            yada:locale
 *     country           yada:country
 *     defaultStorageUrl yada:defaultStorageUrl
 *     storagePrefs      yada:tool-storage-default:{profileId}
 *                       yada:tool-storage:{profileId}:{toolId}
 *     notifPrefs        yada:notif-pref:{appId}
 *     appearance        yada:custom-colors, yada:high-contrast, yada:font-size
 *     llm               yada:llm:config, yada:llm:consent
 *     privacy           yada:search-audit:enabled
 *
 *   DEVICE-LOCAL ONLY  (never synced)
 *     yada:deviceId, yada:access_token, yada:access_token_expires_at,
 *     yada:device:llm, yada:command-history, yada:activeWorkspaces, caches, etc.
 *
 * Usage:
 *   • AuthGuard calls hydrateAccountSettings() after each profile sync —
 *     new devices inherit the user's settings without any wizard.
 *   • AuthGuard calls initAccountSettingsSync() once after login —
 *     any subsequent write dispatches the right DOM event and the setting
 *     is persisted to the auth server in the background.
 */

// ── Types ──────────────────────────────────────────────────────────────────────

import { isDefaultColors } from '../context/ThemeContext';
import { createLogger } from '@yada/logger';

const logger = createLogger('accountSettings');

interface StoragePrefs {
  global?: string;
  tools?: Record<string, string>;
}

interface AccountSettingsMetadata {
  locale?: string;
  country?: string;
  defaultStorageUrl?: string;
  storagePreferences?: StoragePrefs;
  notifPrefs?: Record<string, boolean>;
  customColors?: { bg: string; accent: string; text: string };
  highContrast?: boolean;
  fontSize?: string;
  llmConfig?: Record<string, unknown>;
  encryptedLlmConfig?: { ciphertext: string; iv: string };
  llmConsent?: Record<string, Record<string, boolean>>;
  searchAuditEnabled?: boolean;
  syncDiagnosticsEnabled?: boolean;
  debugCallers?: string[];
  [key: string]: unknown;
}

// ── Key helpers ────────────────────────────────────────────────────────────────

const LOCALE_KEY = 'yada:locale';
const COUNTRY_KEY = 'yada:country';
const STORAGE_URL_KEY = 'yada:defaultStorageUrl';
const NOTIF_PREFIX = 'yada:notif-pref:';
const CUSTOM_COLORS_KEY = 'yada:custom-colors';
const HIGH_CONTRAST_KEY = 'yada:high-contrast';
const FONT_SIZE_KEY = 'yada:font-size';
const LLM_CONFIG_KEY = 'yada:llm:config';
const LLM_CONSENT_KEY = 'yada:llm:consent';
const SEARCH_AUDIT_KEY = 'yada:search-audit:enabled';
const SYNC_DIAGNOSTICS_KEY = 'yada:sync-diagnostics:enabled';
const DEBUG_CALLERS_KEY = 'yada:debug:callers';

function globalStorageKey(profileId: string) {
  return `yada:tool-storage-default:${profileId}`;
}
function toolStoragePrefix(profileId: string) {
  return `yada:tool-storage:${profileId}:`;
}

const FONT_SIZE_PX: Record<string, string> = {
  small: '87.5%',
  medium: '100%',
  large: '112.5%',
  xlarge: '125%',
};

// ── Hydrate ────────────────────────────────────────────────────────────────────

/**
 * Apply auth-server settings to localStorage on a new or re-syncing device.
 */
export function hydrateAccountSettings(
  metadata: Record<string, unknown> | null | undefined,
  profileId: string,
): void {
  if (!metadata) return;
  const m = metadata as AccountSettingsMetadata;

  if (m.locale) {
    localStorage.setItem(LOCALE_KEY, m.locale);
    logger.log('Hydrated locale:', m.locale);
  }

  if (m.country) {
    localStorage.setItem(COUNTRY_KEY, m.country);
    logger.log('Hydrated country:', m.country);
  }

  if (m.defaultStorageUrl) {
    localStorage.setItem(STORAGE_URL_KEY, m.defaultStorageUrl);
    logger.log('Hydrated defaultStorageUrl:', m.defaultStorageUrl);
  }

  const prefs = m.storagePreferences;
  if (prefs) {
    const gk = globalStorageKey(profileId);
    if (prefs.global) {
      localStorage.setItem(gk, prefs.global);
      localStorage.setItem('yada:tool-storage-default', prefs.global);
    }
    if (prefs.tools) {
      const prefix = toolStoragePrefix(profileId);
      for (const [toolId, mode] of Object.entries(prefs.tools)) {
        localStorage.setItem(prefix + toolId, mode);
        localStorage.setItem(`yada:tool-storage:${toolId}`, mode);
      }
    }
    logger.log('Hydrated storage preferences');
  }

  if (m.notifPrefs) {
    for (const [appId, enabled] of Object.entries(m.notifPrefs)) {
      localStorage.setItem(NOTIF_PREFIX + appId, String(enabled));
    }
    logger.log('Hydrated notification preferences');
  }

  if (m.customColors && !isDefaultColors(m.customColors)) {
    localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(m.customColors));
    window.dispatchEvent(new CustomEvent('yada:theme-change'));
    logger.log('Hydrated custom colors');
  } else if (localStorage.getItem(CUSTOM_COLORS_KEY)) {
    localStorage.removeItem(CUSTOM_COLORS_KEY);
    window.dispatchEvent(new CustomEvent('yada:theme-change'));
    logger.log('Reset custom colors to default');
  }

  if (m.highContrast !== undefined) {
    localStorage.setItem(HIGH_CONTRAST_KEY, String(m.highContrast));
    window.dispatchEvent(new CustomEvent('yada:theme-change'));
  }

  if (m.fontSize) {
    localStorage.setItem(FONT_SIZE_KEY, m.fontSize);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-font-size', m.fontSize);
      if (FONT_SIZE_PX[m.fontSize]) {
        document.documentElement.style.fontSize = FONT_SIZE_PX[m.fontSize];
      }
    }
    window.dispatchEvent(new CustomEvent('yada:font-size-change'));
    logger.log('Hydrated font size:', m.fontSize);
  }

  // Hydrate E2EE encrypted LLM configuration if present
  if (m.encryptedLlmConfig) {
    (async () => {
      try {
        const { getCurrentWorkspaceId } = await import('../workspace/workspaceContext.js');
        const workspaceId = getCurrentWorkspaceId() ?? localStorage.getItem('yada:activeWorkspaceId');
        if (workspaceId) {
          const { getWorkspaceSeed } = await import('@yada/sync');
          const seed = await getWorkspaceSeed(workspaceId, false);
          if (seed) {
            const { decryptSecretString } = await import('@yada/crypto');
            const decryptedJson = await decryptSecretString(m.encryptedLlmConfig as any, seed);
            // API keys are kept encrypted on the device (apps/shell/src/llm/llmSecrets.ts).
            const { saveLlmConfig } = await import('../llm/llmSecrets');
            await saveLlmConfig(JSON.parse(decryptedJson)).catch(() => localStorage.setItem(LLM_CONFIG_KEY, decryptedJson));
            window.dispatchEvent(new CustomEvent('yada:llm:changed'));
            logger.log('Decrypted and hydrated zero-knowledge LLM configuration');
          }
        }
      } catch (err) {
        logger.warn('Could not decrypt LLM config with current device seed:', err);
      }
    })();
  } else if (m.llmConfig) {
    localStorage.setItem(LLM_CONFIG_KEY, JSON.stringify(m.llmConfig));
    window.dispatchEvent(new CustomEvent('yada:llm:changed'));
    logger.log('Hydrated public LLM configuration');
  }

  if (m.llmConsent) {
    localStorage.setItem(LLM_CONSENT_KEY, JSON.stringify(m.llmConsent));
    logger.log('Hydrated LLM consent');
  }

  if (m.syncDiagnosticsEnabled !== undefined) {
    localStorage.setItem(SYNC_DIAGNOSTICS_KEY, String(m.syncDiagnosticsEnabled));
  }

  if (m.searchAuditEnabled !== undefined) {
    localStorage.setItem(SEARCH_AUDIT_KEY, String(m.searchAuditEnabled));
  }

  if (m.debugCallers && Array.isArray(m.debugCallers)) {
    localStorage.setItem(DEBUG_CALLERS_KEY, JSON.stringify(m.debugCallers));
    m.debugCallers.forEach(caller => {
      localStorage.setItem(`yada:debug:${caller}`, 'true');
    });
    window.dispatchEvent(new CustomEvent('yada:debug:callers-changed'));
  }
}


// ── Persist ────────────────────────────────────────────────────────────────────

/**
 * Snapshot current localStorage settings and write them to profile metadata.
 * Fire-and-forget — called on every relevant write event.
 */
export async function persistAccountSettings(): Promise<void> {
  const profileId = localStorage.getItem('yada:account:activeProfileId');
  if (!profileId) return;

  const settings: AccountSettingsMetadata = {};

  const locale = localStorage.getItem(LOCALE_KEY);
  if (locale) settings.locale = locale;

  const country = localStorage.getItem(COUNTRY_KEY);
  if (country) settings.country = country;

  const storageUrl = localStorage.getItem(STORAGE_URL_KEY);
  if (storageUrl) settings.defaultStorageUrl = storageUrl;

  // Storage preferences
  const globalMode = localStorage.getItem(globalStorageKey(profileId)) || localStorage.getItem('yada:tool-storage-default');
  const prefix = toolStoragePrefix(profileId);
  const genericPrefix = 'yada:tool-storage:';
  const tools: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)!;
    if (k.startsWith(prefix)) {
      const v = localStorage.getItem(k);
      if (v) tools[k.slice(prefix.length)] = v;
    } else if (k.startsWith(genericPrefix) && !k.startsWith(genericPrefix + 'default')) {
      const remaining = k.slice(genericPrefix.length);
      if (!remaining.includes(':')) {
        const v = localStorage.getItem(k);
        if (v && !tools[remaining]) tools[remaining] = v;
      }
    }
  }
  if (globalMode || Object.keys(tools).length > 0) {
    settings.storagePreferences = {};
    if (globalMode) settings.storagePreferences.global = globalMode;
    if (Object.keys(tools).length > 0) settings.storagePreferences.tools = tools;
  }

  // Notification preferences
  const notifPrefs: Record<string, boolean> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)!;
    if (k.startsWith(NOTIF_PREFIX)) {
      notifPrefs[k.slice(NOTIF_PREFIX.length)] = localStorage.getItem(k) === 'true';
    }
  }
  if (Object.keys(notifPrefs).length > 0) settings.notifPrefs = notifPrefs;

  // Appearance preferences
  const customColors = localStorage.getItem(CUSTOM_COLORS_KEY);
  let resolvedCustomColors: { bg: string; accent: string; text: string } | null = null;
  if (customColors) {
    try {
      const parsed = JSON.parse(customColors);
      if (parsed && !isDefaultColors(parsed)) {
        resolvedCustomColors = { bg: parsed.bg, accent: parsed.accent, text: parsed.text };
      }
    } catch {}
  }
  if (resolvedCustomColors) {
    settings.customColors = resolvedCustomColors;
  }
  const highContrast = localStorage.getItem(HIGH_CONTRAST_KEY);
  if (highContrast !== null) {
    settings.highContrast = highContrast === 'true';
  }
  const fontSize = localStorage.getItem(FONT_SIZE_KEY);
  if (fontSize) settings.fontSize = fontSize;

  // LLM configuration & consent (Zero-Knowledge Encrypted via personal workspace seed)
  const { loadLlmConfig } = await import('../llm/llmSecrets');
  const rawLlmConfig = localStorage.getItem(LLM_CONFIG_KEY) ? JSON.stringify(await loadLlmConfig()) : null;
  if (rawLlmConfig) {
    let encrypted = false;
    try {
      const { getCurrentWorkspaceId } = await import('../workspace/workspaceContext.js');
      const workspaceId = getCurrentWorkspaceId() ?? localStorage.getItem('yada:activeWorkspaceId');
      if (workspaceId) {
        const { getWorkspaceSeed } = await import('@yada/sync');
        const seed = await getWorkspaceSeed(workspaceId, false);
        if (seed) {
          const { encryptSecretString } = await import('@yada/crypto');
          settings.encryptedLlmConfig = await encryptSecretString(rawLlmConfig, seed);
          encrypted = true;
        }
      }
    } catch (err) {
      logger.warn('Workspace seed not available for LLM config encryption:', err);
    }

    if (!encrypted) {
      // If we couldn't encrypt with a seed, we only store provider metadata without raw keys
      try {
        const parsed = JSON.parse(rawLlmConfig);
        const sanitized = {
          provider: parsed.provider?.type === 'local-webllm' ? null : parsed.provider,
          activeRegisteredProviderId: parsed.activeRegisteredProviderId,
          registeredProviders: (parsed.registeredProviders ?? []).map((p: any) => ({
            id: p.id,
            label: p.label,
            baseUrl: p.baseUrl,
            defaultModel: p.defaultModel,
            availableModels: p.availableModels,
          })),
        };
        settings.llmConfig = sanitized;
      } catch {}
    }
  }

  const llmConsent = localStorage.getItem(LLM_CONSENT_KEY);
  if (llmConsent) {
    try {
      settings.llmConsent = JSON.parse(llmConsent);
    } catch {}
  }

  // Search audit
  const syncDiagnostics = localStorage.getItem(SYNC_DIAGNOSTICS_KEY);
  if (syncDiagnostics !== null) settings.syncDiagnosticsEnabled = syncDiagnostics === 'true';

  const searchAudit = localStorage.getItem(SEARCH_AUDIT_KEY);
  if (searchAudit !== null) {
    settings.searchAuditEnabled = searchAudit === 'true';
  }

  // Debug callers
  const debugCallers = localStorage.getItem(DEBUG_CALLERS_KEY);
  if (debugCallers) {
    try {
      const parsed = JSON.parse(debugCallers);
      if (Array.isArray(parsed)) {
        settings.debugCallers = parsed;
      }
    } catch {}
  }

  try {
    const { updateProfile } = await import('./profileService');
    const { getCurrentProfile } = await import('./accountService');
    const currentProfile = await getCurrentProfile();
    const baseMetadata = { ...(currentProfile?.metadata ?? {}) };

    if (!resolvedCustomColors) {
      delete baseMetadata.customColors;
    }

    const mergedMetadata = {
      ...baseMetadata,
      ...settings,
    } as Record<string, unknown>;

    if (!resolvedCustomColors) {
      delete mergedMetadata.customColors;
    }

    await updateProfile(profileId, { metadata: mergedMetadata });
    logger.log('Persisted settings to auth node');
  } catch (err) {
    logger.warn('Persist failed (will retry on next change):', err);
  }
}

// ── Listener init ──────────────────────────────────────────────────────────────

let initialized = false;

/**
 * Register DOM event listeners that trigger a persist on any account-setting write.
 * Call once after the user is authenticated.
 */
export function initAccountSettingsSync(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const persist = () => persistAccountSettings().catch((err) => logger.warn('Persist failed:', err));

  // i18n package dispatches these on language / country change
  window.addEventListener('yada:locale-change', persist);
  window.addEventListener('yada:country-change', persist);

  // Storage-preference writes
  window.addEventListener('yada:storagePref:changed', persist);

  // Appearance & font size
  window.addEventListener('yada:theme-change', persist);
  window.addEventListener('yada:font-size-change', persist);

  // LLM configuration & consent
  window.addEventListener('yada:llm:changed', persist);

  // Generic catch-all
  window.addEventListener('yada:accountSetting:changed', persist);

  // Debug callers changes
  window.addEventListener('yada:debug:callers-changed', persist);

  // The Home introduction is account-wide, rather than profile metadata.
  window.addEventListener('yada:home-introduction-dismissed', () => {
    void persistHomeIntroductionDismissal();
  });

  // Re-hydrate settings on profile switch
  window.addEventListener('yada:profile:changed', async () => {
    try {
      const { getCurrentProfile } = await import('@yada/storage');
      const profile = await getCurrentProfile();
      if (profile) {
        hydrateAccountSettings(profile.metadata, profile.id);
      }
    } catch (err) {
      logger.warn('Failed to re-hydrate on profile switch:', err);
    }
  });

  // Re-hydrate E2EE settings once workspace seed arrives (e.g. after pairing / recovery phrase entry)
  window.addEventListener('yada:workspace:seed-received', async () => {
    try {
      const { getCurrentProfile } = await import('@yada/storage');
      const profile = await getCurrentProfile();
      if (profile) {
        hydrateAccountSettings(profile.metadata, profile.id);
      }
    } catch (err) {
      logger.warn('Failed to re-hydrate after seed received:', err);
    }
  });
}

async function persistHomeIntroductionDismissal(): Promise<void> {
  try {
    const { getAccount, setAccount, getSessionToken } = await import('@yada/storage');
    const account = await getAccount();
    if (!account) return;
    const securityPrefs = { ...(account.securityPrefs ?? {}), homeIntroDismissed: true };
    await setAccount({ ...account, securityPrefs });

    const { getAccessToken } = await import('@yada/api-client');
    const { updateAccount } = await import('@yada/api-client');
    const token = getAccessToken() || await getSessionToken();
    if (token) await updateAccount(token, { security_prefs: securityPrefs });
  } catch (err) {
    logger.warn('Failed to persist Home introduction dismissal:', err);
  }
}
